import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import Graphic from '@arcgis/core/Graphic';
import { Store } from '@ngrx/store';
import Polyline from '@arcgis/core/geometry/Polyline';

import { ScenarioPreprogramme } from '../../../_models/ScenarioPreprogramme';
import { EtapeService } from '../../../_services/http/etape.service';
import { SpService } from '../../../_services/http/sp.service';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import { MainStoreState } from 'src/app/_store/mainStoreState';
import { hasSessionEdition } from 'src/app/_store/SessionStore/session.store';
import { getCurrentUser } from 'src/app/_store/AccountStore/account.store';
import { DashboardService } from '../../dashboard/dashboard.service';

@Component({
  selector: 'app-sp-dialog-edit',
  templateUrl: './sp-dialog-edit.component.html',
  styleUrls: ['./sp-dialog-edit.component.scss'],
})
export class SpDialogEditComponent implements OnInit {
  public form!: FormGroup;
  public saving: boolean = false;
  readonly isReadOnly: boolean = true;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SpDialogEditComponent>,
    private spService: SpService,
    private store: Store<MainStoreState>,
    private dashboardService: DashboardService,
    public etapeService: EtapeService,
    @Inject(MAT_DIALOG_DATA) public data: {sp: ScenarioPreprogramme | undefined; isReadOnly: boolean}
  ) {
    this.isReadOnly = this.data.isReadOnly;
    this.dialogRef.disableClose = true;
    this.createFormGroupForSp();
  }

  ngOnInit(): void {
    this.store.select(getCurrentUser).subscribe((user) => {
      this.store.select(hasSessionEdition(user)).subscribe((sessionEdition) => {
        if (!sessionEdition) {
          this.dashboardService.closeWidget('MapEditComponent');
        }
      });
    });
  }

  public saveChanges(): void {
    if (this.form.dirty) {
      this.saving = true;

      const formResult = this.form.value;

      if (this.form.value.features.length && this.form.value.features[0] instanceof Graphic) {
        const features = formResult.features.map((g: Graphic) => EsriHelpers.GraphicToSagtFeature(g));
        formResult.features = features;
      }

      const sp = new ScenarioPreprogramme(formResult).format();

      if (sp.id) {
        this.spService.update(sp).subscribe((res) => {
          if (res !== undefined) {
            this.dialogRef.close({ save: true });
          }
          this.saving = false;
        });
      } else {
        this.spService.create(sp).subscribe((res) => {
          if (res !== undefined) {
            this.dialogRef.close({ save: true });
          }
          this.saving = false;
        });
      }
    }
  }

  public cancelChanges(): void {
    this.dialogRef.close({ save: false });
  }

  private createFormGroupForSp(): void {
    this.form = this.fb.group({
      id: [this.data.sp?.id],
      nom: [this.data.sp?.nom, Validators.required],
      actif: [this.data.sp?.actif || false],
      idType: new FormControl({ value: this.data.sp?.idType, disabled: this.saving }, Validators.required),
      etapes: [this.data.sp?.etapes],
      features: [this.transformFeatures(), Validators.required],
    });
  }

  private transformFeatures() {
    return this.data.sp?.features.length
      ? this.data.sp.features
          .map((x) => {
            const graphics: Graphic[] = [];
            const graphic = EsriHelpers.SagtFeatureToGraphic(x);
            if (graphic.geometry.type === 'polyline') {
              if ((graphic.geometry as Polyline).paths.length > 1) {
                for (const path of (graphic.geometry as Polyline).paths) {
                  graphics.push(
                    new Graphic({
                      geometry: new Polyline({
                        paths: [path],
                        spatialReference: graphic.geometry.spatialReference,
                      }),
                      attributes: graphic.attributes,
                      symbol: graphic.symbol,
                    })
                  );
                }
              } else {
                graphics.push(graphic);
              }
            } else {
              graphics.push(graphic);
            }

            return graphics;
          })
          .reduce((prev, curr) => prev.concat(curr), [])
      : [];
  }
}
